import { format, formatDistance, differenceInDays, addDays, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isAfter, isBefore, isToday } from 'date-fns';
import type { DateRange } from '@/types';

export const formatDate = (date: Date | string | any): string => {
  if (!date) return 'N/A';
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  // Check if it's a valid Date object
  if (!(d instanceof Date) || isNaN(d.getTime())) return 'Invalid Date';
  
  return format(d, 'MMM dd, yyyy');
};

export const formatDateTime = (date: Date | string | any): string => {
  if (!date) return 'N/A';
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!(d instanceof Date) || isNaN(d.getTime())) return 'Invalid Date';
  
  return format(d, 'MMM dd, yyyy HH:mm');
};

export const formatDateShort = (date: Date | string | any): string => {
  if (!date) return 'N/A';
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!(d instanceof Date) || isNaN(d.getTime())) return 'Invalid Date';
  
  return format(d, 'MM/dd/yyyy');
};

export const formatRelativeTime = (date: Date | string | any): string => {
  if (!date) return 'N/A';
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!(d instanceof Date) || isNaN(d.getTime())) return 'Invalid Date';
  
  return formatDistance(d, new Date(), { addSuffix: true });
};

export const getDaysAgo = (date: Date | string | any): number => {
  if (!date) return 0;
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!(d instanceof Date) || isNaN(d.getTime())) return 0;
  
  return differenceInDays(new Date(), d);
};

export const getDaysUntil = (date: Date | string | any): number => {
  if (!date) return 0;
  
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!(d instanceof Date) || isNaN(d.getTime())) return 0;
  
  return differenceInDays(d, new Date());
};

export const getTodayRange = (): DateRange => ({
  start: startOfDay(new Date()),
  end: endOfDay(new Date()),
});

export const getWeekRange = (): DateRange => ({
  start: startOfWeek(new Date()),
  end: endOfWeek(new Date()),
});

export const getMonthRange = (): DateRange => ({
  start: startOfMonth(new Date()),
  end: endOfMonth(new Date()),
});

export const getCustomRange = (days: number): DateRange => ({
  start: subDays(new Date(), days),
  end: new Date(),
});

export const isInRange = (date: Date, range: DateRange): boolean => {
  return !isBefore(date, range.start) && !isAfter(date, range.end);
};

export const predictNextPurchaseDate = (
  lastPurchaseDate: Date,
  averageDaysBetweenPurchases: number
): Date => {
  return addDays(lastPurchaseDate, Math.round(averageDaysBetweenPurchases));
};

export const isOverdue = (predictedDate: Date): boolean => {
  return isBefore(predictedDate, new Date()) && !isToday(predictedDate);
};
